import { PositionActionKey } from '../models/position-action.model'
import { RowOrigin } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * The backend's per-row `availableActions` (PVAL-26401) is the authority on "is this ROW eligible";
 * the UI adds only the checks the client alone can make (the live cross-grid selection). This file
 * is the whole of the row half.
 *
 * Rules, from PVAL-26403:
 *  - `availableActions` ABSENT, or present and EMPTY → no per-row restriction: every item the row's
 *    source renders is enabled, subject only to the local gates (Ignore/Unignore on
 *    Prometheus-origin rows only; Unmatch only when the row is matched).
 *  - `availableActions` NON-EMPTY → it decides: an item is enabled only if the list names it.
 *
 * Which is why a non-empty list of unrecognised tokens disables everything rather than enabling
 * everything: "the backend said something and we could not read it" must fail closed. Add the new
 * spelling to {@link ACTION_BY_TOKEN} instead.
 */

/**
 * Token → action. Matched after case-folding and stripping every non-letter, so `MATCH_ADDITIONAL`,
 * `matchAdditional` and `match-additional` all land on the same key.
 *
 * `matchAdditional` maps to `match` because they are one action with two labels (see
 * {@link PositionActionKey}) — a backend that distinguishes them still enables the same item.
 */
const ACTION_BY_TOKEN: Record<string, PositionActionKey> = {
  match: 'match',
  matchadditional: 'match',
  addtomatch: 'match',
  unmatch: 'unmatch',
  ignore: 'ignore',
  unignore: 'unignore',
}

/**
 * Origins that may be ignored — Prometheus (Custody + Trading) only.
 *
 * Set by the DESIGN: the action matrix in `Positions-2.0-Feature-Differences.md` §4 marks
 * Ignore/Unignore Prometheus-only, and the prototype's `setIgnore` refuses any row whose `src` is
 * not `cust`/`trad` (`Positions 2.0.dc.html:1072`). `MatchingService.ignore` applies the same rule
 * locally, and `utils/action-request.ts` buckets the ids to match.
 *
 * ⚠ The actions endpoint currently disagrees — it reads `enfnPosIds` only for an ignore. See
 * `utils/action-request.ts` for exactly what that means for a dispatched Ignore today.
 */
const IGNORABLE_ORIGINS: RowOrigin[] = ['cust', 'trad']

/** Normalise one raw token, or `undefined` when it is not an action this UI knows. */
export function normalizeAction(token: string): PositionActionKey | undefined {
  return ACTION_BY_TOKEN[token.toLowerCase().replace(/[^a-z]/g, '')]
}

/** Whether the backend says this one row is eligible for `action`. See the file header for the rules. */
export function rowAllowsAction(
  row: WorkbenchRow,
  action: PositionActionKey,
): boolean {
  const declared = row.availableActions
  if (!declared?.length) return true
  return declared.some(token => normalizeAction(token) === action)
}

/**
 * Whether a whole context-menu scope is eligible for `action`: EVERY row must allow it, and an empty
 * scope allows nothing.
 *
 * `every`, not `some`: the dispatch sends one request for the whole scope, and the backend validates
 * each row, so enabling an item that one selected row is ineligible for buys a guaranteed partial
 * failure. A mixed selection therefore offers no bulk action — deliberately stricter than
 * transactions-2, which has no per-row eligibility to consult.
 */
export function scopeAllowsAction(
  rows: WorkbenchRow[],
  action: PositionActionKey,
): boolean {
  return rows.length > 0 && rows.every(row => rowAllowsAction(row, action))
}

/**
 * Whether a Match click would ADD to an existing group rather than create a new one — i.e. whether
 * the item reads "Match Additional".
 *
 * Taken from the prototype (`Positions 2.0.dc.html`, the `alreadyHasSrc` computation repeated in
 * each of its three source branches):
 *
 *     const otherSrcs     = new Set(<other grid's selected rows>.map(x => x.src))
 *     const alreadyHasSrc = r.group && s.rows.some(x => x.group === r.group && otherSrcs.has(x.src))
 *
 * So BOTH must hold:
 *  1. the RIGHT-CLICKED row already belongs to a group, and
 *  2. that group already has a member whose ORIGIN is one the other grid has selected.
 *
 * Condition 2 is the part worth spelling out. "Match Additional" does not mean "this row is matched"
 * — it means the group ALREADY reaches the source you are now matching to, so the click extends an
 * existing relationship instead of forming one. A row matched only to Custody, right-clicked while
 * an Enfusion row is selected, still reads plain "Match": that click brings a source the group does
 * not yet have.
 *
 * Origins, not grid sources: the prototype compares `x.src`, so Custody and Trading count as
 * distinct members even though both fold to the `prom` grid source.
 *
 * Note this keys off the CLICKED row, not the whole scope — the previous implementation used
 * `scopeRows.some(row => !!row.group)`, which relabelled on any grouped row in a multi-row
 * selection and ignored the other grid entirely.
 *
 * @param clickedRow the row whose context menu is open, or undefined when the click missed a row
 * @param groupMembers every loaded row sharing a given group id
 * @param otherSelectedOrigins origins selected in the OTHER grid
 * @returns true when the Match item should read "Match Additional"
 */
export function matchAdditionalApplies(
  clickedRow: WorkbenchRow | undefined,
  groupMembers: (group: string) => WorkbenchRow[],
  otherSelectedOrigins: ReadonlySet<RowOrigin>,
): boolean {
  const group = clickedRow?.group
  if (!group || otherSelectedOrigins.size === 0) return false
  return groupMembers(group).some(member =>
    otherSelectedOrigins.has(member.origin),
  )
}

/**
 * Whether every row in the scope can carry an ignore/unignore at all. A local gate that mirrors the
 * design: Ignore/Unignore are Prometheus-only regardless of what `availableActions` says, because
 * that is what the action matrix specifies and what `MatchingService.ignore` enforces — it silently
 * skips any other origin.
 */
export function scopeIsIgnorable(rows: WorkbenchRow[]): boolean {
  return (
    rows.length > 0 && rows.every(row => IGNORABLE_ORIGINS.includes(row.origin))
  )
}
